/**
 * @file   src\Pages\Payment.tsx
 * @brief  Payment page.
 * @date   JUN, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React, useState, Button, useNavigate, Link, useParams, useEffect, Modal } from '../utils/thirdpartyComponents';
import CWFooter from "src/components/CWFooter";
import CWInput from "src/components/CWInput";
import InputCheck from "src/components/CWChecBoxLabel";
import Gobackicon from "../assets/images/left-arrow-small.svg";
import "src/styles/inner-page-styles.scss";
import { EMAIL_MAX_LENGTH, PROFILE_NAME_MAX_LENGTH } from '../utils/constants';
import {formatPhoneNumber} from "../utils/helper";
import * as Yup from 'yup';
import { MyprofileSchema } from "../validations/myprofileSchema";
import { pageURLs } from 'src/utils/pageUrls';
import { IMyprofile, IMyprofileData } from '../interfaces/generalInterface';
import axios from '../config/axiosConfig';
import { ProfileApi} from "../utils/apiUrls";
import { MessageToaster } from "src/utils/toastUtil";
import SmallLoader from '../components/SmallLoader';

const MyProfile = () => {

    const navigate = useNavigate();
    // Instantiate MessageToaster
    const toaster = new MessageToaster();
    const [loading, setLoading] = useState(false);

    const params = useParams();
    // State to profile details 
    const [profileDta, setProfileData] = useState<IMyprofile>({
        UserID:0,
        FullName: "",
        Email: "",
        Phone: "",
        IsSmsSubscribed:0
    });

    const fetchProfileData = async () => {
        // Instantiate MessageToaster
        const toaster = new MessageToaster();
      
        try {
          setLoading(true); // Show loader
          const responseProfile = await axios.get(ProfileApi.PROFILE_FETCH);
      
          if (responseProfile?.data?.error === 0) {
            const profileData: IMyprofile = responseProfile.data.data;
            setProfileData(profileData);
          } else {
            toaster.toastError(responseProfile?.data?.message);
          }
        } catch (error: any) {
          throw new Error(error.response?.data?.message || error.message);
        } finally {
            setLoading(false); // Hide loader
          }
      };
      
      useEffect(() => {
        fetchProfileData();
      }, []);

    // Initialise states for errors
    const [errors, setErrors] = useState({
        FullName: '',
        Email: '',
    });


    // Triggers whenever input is entered
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        setProfileData({
            ...profileDta,
            [name]: value,
        });
    }

    const [checkboxname, setCheckboxname] = useState('IsSmsSubscribed');
    const [checkboxvalue, setCheckboxvalue] = useState(profileDta.IsSmsSubscribed === 1 ? true : false);
    const [showcheckboxModal, setShowcheckModal] = useState(false);
    const handleClosecheckboxModal = () => setShowcheckModal(false);
    const handleShowcheckboxModal = (checked: boolean, name: string) => 
        {
            if(checked === true)
            {
                setShowcheckModal(true);
                setCheckboxname(name);
                setCheckboxvalue(checked);
            }
            else
            {
                handleCheckboxChange(checked,name);
            }
            
        }

    // Checkbox change handler
    const handleCheckboxChange = (checked: boolean, name: string) => {
        // Update form data with the checkbox state
        setProfileData({
            ...profileDta,
            [name]: checked === false ? 1 : 0,  // Assign the boolean value directly
        });
        handleClosecheckboxModal();
    };

    // Triggers when sign in is done
    const handleSubmit = async () => {
        try {
            setLoading(true); // Show loader

            // Trim the full name before validation and submission
            const trimmedProfileData = {
                ...profileDta,
                FullName: profileDta.FullName.trim(),
            };

            // Validate form data
            await MyprofileSchema.validate(trimmedProfileData, { abortEarly: false });
            try 
            {
                const requestProfileData: IMyprofileData = {
                    name: trimmedProfileData.FullName,
                    email: trimmedProfileData.Email,
                    IsSmsSubscribed: trimmedProfileData.IsSmsSubscribed
                };
                const responseProfileUpdate = await axios.post(ProfileApi.PROFILE_UPDATE,requestProfileData);
                if(responseProfileUpdate?.data?.error === 0)
                {
                     //Clear errors
                    setErrors({
                        FullName: '',
                        Email: '',
                    });
                    navigate("/"+params.carwashname+"/"+pageURLs.settings);
                }     
                else
                {
                    toaster.toastError(responseProfileUpdate?.data?.message);
                }
            } 
            catch (error : any) {
                throw new Error(error.response?.data?.message || error.message);
            }
        } catch (error) {
            //If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                const newErrors: any = {};
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof profileDta] = err.message;
                });
                setErrors(newErrors);
            }
        } finally {
            setLoading(false); // Hide loader
          }
    }


    return (

        <div className="common-content-wrap pb-170">
            <div className="inner-content-wrap">
                <div className="common-header two-block-header">
                    <div className="pe-4 ps-4 pt-0 pb-0 h-100">
                        <div className="common-header-inner">
                            <Link to={"/"+params.carwashname+"/"+pageURLs.settings} className="back-link"><img src={Gobackicon} alt="Car Wash App" /></Link> Edit Profile
                        </div>
                    </div>
                </div>
                <div className="main-content-wrap pe-0 ps-0 pt-0">
                    <div className="profile-content-wrap">
                        <CWInput
                            id="profilename"
                            name="FullName"
                            type="text"
                            label="Name"
                            isMandatory
                            value={profileDta.FullName}
                            maxLength={PROFILE_NAME_MAX_LENGTH}
                            errorMessage={errors.FullName && errors.FullName}
                            onChange={handleInputChange}
                        />
                        <CWInput
                            id="phonenumber"
                            name="phonenumber"
                            type="text"
                            label="Phone Number"
                            value={formatPhoneNumber(profileDta.Phone)}
                            errorMessage=""
                            onChange={(e) => { }}
                            disabled
                        />
                        <CWInput
                            id="email"
                            name="Email"
                            type="email"
                            label="Email Address"
                            isMandatory
                            value={profileDta.Email}
                            maxLength={EMAIL_MAX_LENGTH}
                            errorMessage={errors.Email && errors.Email}
                            onChange={handleInputChange}
                        />
                        <InputCheck 
                            id="receivesms"
                            name="receivesms"
                            label="Opt-Out of SMS notifications."
                            type="checkbox"
                            checked={profileDta.IsSmsSubscribed === 1 ? false : true}
                            onChange={(e) => handleShowcheckboxModal(e.target.checked, 'IsSmsSubscribed')}
                        />
                    </div>
                </div>
            </div>
            <div className="btm-secondarybtn-wrap btm-fixed-wrap">
            {!loading && 
                <div className="btm-inner-wrap">
                    <Button variant="primary" className="primary-btn btn-main" onClick={handleSubmit}>Save</Button>
                </div>
            }
            </div>
            {loading && <SmallLoader />}
            <CWFooter activeLink="Settings"/>
            <Modal 
                show={showcheckboxModal} 
                onHide={handleClosecheckboxModal}
                centered
                className='notification-modal'
            >
            <Modal.Body className='text-center'>
                <h6>Are you sure you want to opt out of receiving messages?</h6>
            </Modal.Body>
            <Modal.Footer className='d-grid'>
                <Button variant="light" className="m-0" onClick={handleClosecheckboxModal}>No</Button>
                <Button variant="primary" className='primary-btn btn-main m-0' onClick={() => handleCheckboxChange(checkboxvalue,checkboxname)}>Yes</Button>
            </Modal.Footer>
            </Modal>
        </div>
    )
}

export default MyProfile;
